import { forwardRef, useCallback, useEffect, useRef, useImperativeHandle, useState } from "react";
import { handlePaste, type PasteMode } from "../../lib/utils/pasteHandler";
import { applyFormatting } from "../../lib/utils/textFormatting";
import { isCursorAtStart, isCursorAtEnd } from "./utils/cursorUtils";

interface ContentEditorProps {
  id: string;
  placeholder?: string;
  isActive: boolean;
  pasteMode: PasteMode;
  /** Whether the editor is editable. When false, content cannot be modified. Defaults to true. */
  editable?: boolean;
  onContentChange: (id: string, content: string) => void;
  onEnter: (id: string, contentBeforeCursor?: string, contentAfterCursor?: string) => void;
  onBackspace: (id: string, currentContent?: string) => void;
  onFocus: (id: string) => void;
  onBlur: () => void;
  onArrowUp: (id: string) => void;
  onArrowDown: (id: string) => void;
  onCommand: (id: string, command: string) => void;
  onHighlightClick: (e: React.MouseEvent) => void;
  stripExistingHighlights: (html: string) => string;
  onMultilinePaste?: (id: string, lines: string[]) => void;
  /** Called after input to clean up invalid highlights */
  onInputCleanup?: (element: HTMLDivElement) => void;
}

/**
 * ContentEditor component handles the contentEditable div and basic editing functionality
 */
export const ContentEditor = forwardRef<HTMLDivElement, ContentEditorProps>(({
  id,
  placeholder = "Type '/' for commands",
  isActive,
  pasteMode,
  editable = true,
  onContentChange,
  onEnter,
  onBackspace,
  onFocus,
  onBlur,
  onArrowUp,
  onArrowDown,
  onCommand,
  onHighlightClick,
  stripExistingHighlights,
  onMultilinePaste,
  onInputCleanup
}, ref) => {
  // Local ref for internal use (the forwarded ref can't be read directly)
  const localRef = useRef<HTMLDivElement>(null);
  
  // Sync the forwarded ref with our local ref
  useImperativeHandle(ref, () => localRef.current!, []);
  
  // Track hover and focus states for placeholder visibility
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    
    // Prevent browser's native undo/redo (will be handled at Editor level)
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
    
    if (isCtrlOrCmd && e.key === 'z') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    if ((isCtrlOrCmd && e.key === 'z' && e.shiftKey) || (isCtrlOrCmd && e.key === 'y')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Handle Ctrl+A (Select All)
    if (isCtrlOrCmd && e.key === 'a') {
      const selection = window.getSelection();
      if (!selection) return;
      
      // Check if current block is fully selected
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      if (range) {
        const selectedText = selection.toString();
        const allText = element.textContent || '';
        
        // If entire block is already selected, trigger select all blocks
        if (selectedText.length > 0 && selectedText.trim() === allText.trim()) {
          e.preventDefault();
          
          // Trigger custom event for "select all blocks"
          const selectAllEvent = new CustomEvent('selectAllBlocks', { bubbles: true });
          element.dispatchEvent(selectAllEvent);
          return;
        }
      }
      // Otherwise, let native Ctrl+A select the current block
    }

    // Handle typing when text is selected - replace selection with typed character
    const selection = window.getSelection();
    const hasSelection = selection && !selection.isCollapsed && selection.toString().length > 0;
    
    if (hasSelection && e.key.length === 1 && !isCtrlOrCmd && !e.altKey && !e.metaKey) {
      // This is a printable character being typed with text selected
      e.preventDefault();
      
      // Delete the selected content and insert the new character
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Insert the new character
        const textNode = document.createTextNode(e.key);
        range.insertNode(textNode);
        
        // Move cursor after the inserted character
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Trigger content change
        onContentChange(id, element.innerHTML);
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Get the current selection to split content at cursor position
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create a range from the start of the element to the cursor position
        const rangeBeforeCursor = document.createRange();
        rangeBeforeCursor.setStart(element, 0);
        rangeBeforeCursor.setEnd(range.startContainer, range.startOffset);
        
        // Create a range from the cursor position to the end of the element
        const rangeAfterCursor = document.createRange();
        rangeAfterCursor.setStart(range.endContainer, range.endOffset);
        rangeAfterCursor.setEnd(element, element.childNodes.length);
        
        // Extract content before cursor
        const fragmentBefore = rangeBeforeCursor.cloneContents();
        const tempDivBefore = document.createElement('div');
        tempDivBefore.appendChild(fragmentBefore);
        const contentBeforeCursor = tempDivBefore.innerHTML;
        
        // Extract content after cursor
        const fragmentAfter = rangeAfterCursor.cloneContents();
        const tempDivAfter = document.createElement('div');
        tempDivAfter.appendChild(fragmentAfter);
        const contentAfterCursor = tempDivAfter.innerHTML;
        
        // Call onEnter with both content parts
        onEnter(id, contentBeforeCursor, contentAfterCursor);
      } else {
        // Fallback if no selection - just create empty new block
        onEnter(id, element.innerHTML, '');
      }
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Handle Shift+Enter for list continuation - create new list item
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Find the closest list item - handle various cursor positions
        let listItem: Element | null = null;
        const startNode = range.startContainer;
        
        // If we're in a text node, use its parent
        if (startNode.nodeType === Node.TEXT_NODE) {
          listItem = startNode.parentElement?.closest('li') || null;
        } else {
          // If we're in an element node (e.g., between spans), search for li
          listItem = (startNode as Element).closest('li');
          
          // If not found and we have a parent, try the parent
          if (!listItem && startNode.parentElement) {
            listItem = startNode.parentElement.closest('li');
          }
        }
        
        // Also check if the common ancestor is within a list item
        if (!listItem && range.commonAncestorContainer) {
          const ancestor = range.commonAncestorContainer;
          if (ancestor.nodeType === Node.TEXT_NODE) {
            listItem = ancestor.parentElement?.closest('li') || null;
          } else {
            listItem = (ancestor as Element).closest('li');
          }
        }
        
        if (listItem) {
          e.preventDefault();
          
          // Create a new list item
          const newListItem = document.createElement('li');
          
          // Get the parent list (ul or ol)
          const parentList = listItem.parentElement;
          
          if (parentList) {
            // Insert the new list item after the current one
            parentList.insertBefore(newListItem, listItem.nextSibling);
            
            // Move any content after the cursor to the new list item
            const remainingContent = range.extractContents();
            if (remainingContent.textContent?.trim()) {
              newListItem.appendChild(remainingContent);
            } else {
              // If no content was extracted, add a placeholder to ensure the list item is editable
              newListItem.innerHTML = '<br>';
            }
            
            // Position cursor at the start of the new list item
            const newRange = document.createRange();
            
            // If the new list item has a br, position before it; otherwise position at start
            if (newListItem.firstChild) {
              newRange.setStart(newListItem, 0);
              newRange.setEnd(newListItem, 0);
            } else {
              // Empty list item - insert a text node for cursor positioning
              const textNode = document.createTextNode('');
              newListItem.appendChild(textNode);
              newRange.setStart(textNode, 0);
              newRange.setEnd(textNode, 0);
            }
            
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            // Trigger content change
            onContentChange(id, element.innerHTML);
          }
        }
      }
    } else if (e.key === 'Backspace') {
      // Check for text selection - if any text is selected, let the browser handle deletion
      const selection = window.getSelection();
      const hasSelection = selection && !selection.isCollapsed;
      
      if (hasSelection) {
        // Allow default behavior - browser will delete the selected text
        return;
      }
      
      // Check if we're inside a list item
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let listItem: Element | null = null;
        
        // Find the closest list item
        const startNode = range.startContainer;
        if (startNode.nodeType === Node.TEXT_NODE) {
          listItem = startNode.parentElement?.closest('li') || null;
        } else {
          listItem = (startNode as Element).closest('li');
        }
        
        // If we're in a list item, check if it's empty and at the start
        if (listItem) {
          const listItemText = listItem.textContent?.trim() || '';
          const isListItemEmpty = listItemText === '';
          
          // Check if cursor is at the start of the list item
          const isAtListItemStart = (() => {
            const range = selection.getRangeAt(0);
            const preRange = document.createRange();
            preRange.selectNodeContents(listItem);
            preRange.setEnd(range.startContainer, range.startOffset);
            const textBefore = preRange.toString().trim();
            return textBefore.length === 0;
          })();
          
          if (isListItemEmpty || isAtListItemStart) {
            e.preventDefault();
            
            // Get the parent list
            const parentList = listItem.parentElement;
            
            if (parentList && (parentList.tagName === 'UL' || parentList.tagName === 'OL')) {
              // Check if this is the only list item
              const listItems = parentList.querySelectorAll('li');
              
              if (listItems.length === 1) {
                // Last list item - remove the entire list and convert to paragraph
                const listContent = listItem.innerHTML;
                parentList.remove();
                
                // If the list item was empty, let the block be empty
                if (isListItemEmpty) {
                  element.innerHTML = '';
                } else {
                  element.innerHTML = listContent;
                }
                
                onContentChange(id, element.innerHTML);
              } else {
                // Multiple list items - just remove this one
                listItem.remove();
                
                // Move cursor to the previous list item if it exists
                const remainingItems = parentList.querySelectorAll('li');
                if (remainingItems.length > 0) {
                  const prevItem = remainingItems[remainingItems.length - 1];
                  const newRange = document.createRange();
                  const sel = window.getSelection();
                  
                  // Position cursor at the end of the previous item
                  if (prevItem.lastChild) {
                    if (prevItem.lastChild.nodeType === Node.TEXT_NODE) {
                      newRange.setStart(prevItem.lastChild, prevItem.lastChild.textContent?.length || 0);
                    } else {
                      newRange.setStart(prevItem, prevItem.childNodes.length);
                    }
                    newRange.collapse(true);
                    sel?.removeAllRanges();
                    sel?.addRange(newRange);
                  }
                }
                
                onContentChange(id, element.innerHTML);
              }
              return;
            }
          }
        }
      }
      
      // Check if cursor is at the start of the block or block is empty
      const isEmpty = element.innerText === '' || element.innerText === '\n';
      const atStart = isCursorAtStart(element);
      
      if (isEmpty || atStart) {
        e.preventDefault();
        // Pass current content so it can be merged with previous block
        const currentContent = element.innerHTML;
        onBackspace(id, isEmpty ? '' : currentContent);
      }
    } else if (e.key === 'Delete') {
      // Check for text selection - if any text is selected, let the browser handle deletion
      const selection = window.getSelection();
      const hasSelection = selection && !selection.isCollapsed;
      
      if (hasSelection) {
        // Allow default behavior - browser will delete the selected text
        return;
      }
      // Otherwise, let the browser handle the Delete key normally
      // (no special block-level behavior needed for Delete key without selection)
    } else if (e.key === 'ArrowUp') {
      if (isCursorAtStart(element)) {
        e.preventDefault();
        onArrowUp(id);
      }
    } else if (e.key === 'ArrowDown') {
      if (isCursorAtEnd(element)) {
        e.preventDefault();
        onArrowDown(id);
      }
    }
  }, [id, onEnter, onBackspace, onArrowUp, onArrowDown, onContentChange]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    
    // Clean up any invalid highlights (e.g., when typing expanded a highlight span)
    // This needs to happen BEFORE we save the content
    if (onInputCleanup) {
      onInputCleanup(element);
    }
    
    // Clean up empty list items (but preserve the list structure if there are non-empty items)
    const emptyListItems = element.querySelectorAll('li');
    let hasNonEmptyListItem = false;
    
    emptyListItems.forEach(li => {
      const text = li.textContent?.trim() || '';
      if (text.length > 0) {
        hasNonEmptyListItem = true;
      }
    });
    
    // If all list items are empty, remove the list structure
    if (emptyListItems.length > 0 && !hasNonEmptyListItem) {
      const lists = element.querySelectorAll('ul, ol');
      lists.forEach(list => {
        // Only remove if all items are truly empty
        const items = list.querySelectorAll('li');
        const allEmpty = Array.from(items).every(li => (li.textContent?.trim() || '') === '');
        if (allEmpty) {
          list.remove();
        }
      });
    }
    
    // Save the content WITH highlights so the backend can extract relationships
    const currentContent = element.innerHTML;
    onContentChange(id, currentContent);
    
    // Check for command trigger
    const textContent = element.innerText;
    if (textContent.endsWith('/')) {
      onCommand(id, '/');
    }
  }, [id, onContentChange, onCommand, onInputCleanup]);

  const handlePasteEvent = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    // Get plain text from clipboard
    const text = e.clipboardData.getData("text/plain");
    
    // Check if text contains multiple lines
    const lines = text.split(/\r?\n/);
    
    // If there are multiple lines and we have a multiline paste handler, use it
    if (lines.length > 1 && onMultilinePaste) {
      e.preventDefault();
      // Filter out empty lines but keep lines with just spaces (user might want them)
      const nonEmptyLines = lines.filter(line => line.trim() !== '' || lines.length === 2);
      if (nonEmptyLines.length > 1) {
        onMultilinePaste(id, nonEmptyLines);
        return;
      }
    }
    
    // Otherwise, use the standard paste handler
    handlePaste(e, pasteMode, (content) => {
      // Save pasted content (will be highlighted automatically on next render)
      onContentChange(id, content);
    });
  }, [id, pasteMode, onContentChange, onMultilinePaste]);

  const handleFocusEvent = useCallback(() => {
    setIsFocused(true);
    onFocus(id);
  }, [id, onFocus]);
  
  const handleBlurEvent = useCallback(() => {
    setIsFocused(false);
    onBlur();
  }, [onBlur]);
  
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Selection change handler - refocus editor if it has selection but lost focus
  useEffect(() => {
    if (!editable) return;
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const editorEl = localRef.current;
      if (selection && editorEl && selection.rangeCount > 0) {
        const isInEditor = editorEl.contains(selection.getRangeAt(0).commonAncestorContainer);
        
        // If there's a selection in this editor but focus is elsewhere, refocus
        if (!selection.isCollapsed && isInEditor && document.activeElement !== editorEl) {
          editorEl.focus();
        }
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [editable]);

  // Document-level mouseup handler to ensure focus returns to editor after selection
  useEffect(() => {
    if (!editable) return;
    
    const handleDocumentMouseUp = () => {

      const selection = window.getSelection();
      const editorEl = localRef.current;
      
      // Check if there's a selection and it's within our contentEditable
      if (selection && !selection.isCollapsed && editorEl && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const isSelectionInEditor = editorEl.contains(range.commonAncestorContainer);
        
        if (isSelectionInEditor && document.activeElement !== editorEl) {
          // Refocus the editor while preserving selection
          editorEl.focus();
        }
      }
    };
    
    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => document.removeEventListener('mouseup', handleDocumentMouseUp);
  }, [editable]);

  // Show placeholder only when empty AND (focused OR hovered)
  const showPlaceholder = editable && (isFocused || isHovered);
  
  return (
    <div
      ref={localRef}
      contentEditable={editable}
      dir="auto"
      onInput={editable ? handleInput : undefined}
      onKeyDown={editable ? handleKeyDown : undefined}
      onFocus={editable ? handleFocusEvent : undefined}
      onBlur={editable ? handleBlurEvent : undefined}
      onPaste={editable ? handlePasteEvent : undefined}
      onClick={onHighlightClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-placeholder={editable ? placeholder : undefined}
      className={`border-none outline-none bg-transparent text-editor-text font-normal text-base leading-relaxed min-h-[1.5rem] whitespace-pre-wrap break-words ${
        isActive ? 'ring-0' : ''
      } ${showPlaceholder ? 'empty:before:content-[attr(data-placeholder)] empty:before:text-editor-text-placeholder empty:before:pointer-events-none' : ''} ${!editable ? 'cursor-default' : ''}`}
      style={{ 
        minHeight: '1.5rem',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        userSelect: 'text',  // Enable text selection to span across blocks
        WebkitUserSelect: 'text',
      }}
    />
  );
});

ContentEditor.displayName = 'ContentEditor';
